import { useEffect, useState } from 'react';
import { CreditCard, ExternalLink, Zap, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';

interface PlanLimits {
  tokens_per_month: number;
}

interface Plan {
  id: string;
  name: string;
  priceCents: number;
  limits: PlanLimits;
  polar_product_id: string | null;
}

interface SubscriptionInfo {
  subscription: {
    planId: string;
    status: string;
    currentPeriodEnd: string | null;
    tokensUsed: number;
    tokensLimit: number;
    cancelAtPeriodEnd: boolean;
    cardBrand: string | null;
    cardLastFour: string | null;
  };
  plan: Plan | null;
}

export function BillingPage() {
  const [info, setInfo] = useState<SubscriptionInfo | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    Promise.all([
      apiGet<SubscriptionInfo>('/billing/subscription'),
      apiGet<Plan[]>('/billing/plans'),
    ]).then(([sub, pls]) => {
      setInfo(sub);
      setPlans(pls);
    }).finally(() => setLoading(false));
  }, []);

  async function handleUpgrade(productId: string) {
    setUpgrading(true);
    try {
      const { url } = await apiPost<{ url: string }>('/billing/checkout', { productId });
      window.location.href = url;
    } catch (err) {
      console.error('Checkout failed:', err);
    } finally {
      setUpgrading(false);
    }
  }

  async function handleManage() {
    try {
      const { url } = await apiGet<{ url: string }>('/billing/portal');
      window.open(url, '_blank');
    } catch (err) {
      console.error('Portal failed:', err);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  const sub = info?.subscription;
  const plan = info?.plan;
  const isFree = sub?.planId === 'free';

  return (
    <div className="space-y-8 max-w-3xl mx-auto w-full animate-fade-in">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Billing</h1>
        <p className="text-sm text-base-content/40 mt-1">Manage your subscription and token usage.</p>
      </div>

      {/* Current Plan */}
      <div className="space-y-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
          Current Plan
        </h2>
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Zap size={20} className="text-primary" />
              </div>
              <div>
                <p className="font-semibold">{plan?.name ?? 'Free'}</p>
                <p className="text-xs text-base-content/40">
                  {isFree
                    ? 'Free tier'
                    : `€${((plan?.priceCents ?? 0) / 100).toFixed(0)}/mo`
                  }
                  {sub?.cancelAtPeriodEnd && (
                    <span className="text-warning ml-2">Cancels at period end</span>
                  )}
                </p>
              </div>
            </div>
            {!isFree && (
              <button onClick={handleManage} className="btn btn-sm btn-ghost gap-1">
                Manage <ExternalLink size={12} />
              </button>
            )}
          </div>

          {sub?.cardBrand && sub?.cardLastFour && (
            <div className="mt-3 pt-3 border-t border-base-300 flex items-center gap-2 text-xs text-base-content/50">
              <CreditCard size={14} />
              <span>{sub.cardBrand} ending in {sub.cardLastFour}</span>
            </div>
          )}
        </div>
      </div>

      {/* Available Plans */}
      {isFree && plans.length > 1 && (
        <div className="space-y-1">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
            Upgrade
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {plans
              .filter((p) => p.id !== 'free' && p.polar_product_id)
              .map((p) => (
                <div key={p.id} className="glass-card rounded-xl p-5 flex flex-col">
                  <h3 className="font-semibold">{p.name}</h3>
                  <p className="text-2xl font-bold mt-1">
                    €{(p.priceCents / 100).toFixed(0)}
                    <span className="text-sm font-normal text-base-content/40">/mo</span>
                  </p>
                  <ul className="text-xs text-base-content/60 mt-3 space-y-1 flex-1">
                    <li>{formatLimit(p.limits.tokens_per_month)} tokens/mo</li>
                  </ul>
                  <button
                    onClick={() => handleUpgrade(p.polar_product_id!)}
                    disabled={upgrading}
                    className="btn btn-primary btn-sm mt-4 w-full"
                  >
                    {upgrading ? <Loader2 size={14} className="animate-spin" /> : 'Upgrade'}
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatLimit(n: number): string {
  if (n === -1) return 'Unlimited';
  return n.toLocaleString();
}
